// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { connect } from "react-redux";
import { defineMessages } from "react-intl";
import { Chart as ChartJS, ChartData, ChartOptions, ScatterDataPoint } from "chart.js";
import { AnnotationOptions } from "chartjs-plugin-annotation";

import { isSet, formatTimestamp } from "shared/utils/utils";
import { getColor, getGraphBoundaryDatesFromPeriod } from "guiutils";
import { IState } from "states/states";
import { IOutput, OutputMode } from "shared/types/parameter";
import { IIoTrend } from "states/trendingstates";

import IoGraphBaseComponent, {
  IoChartType,
  mapBaseStateToProps,
  mapDispatchToProps,
} from "./IoGraphBase";

import cssContent from "constants.module.scss";

const MAX_LINE_WIDTH = 50;
const FONTSIZE_REDUCTION = 0.1;
const OFFSET_LINECOUNT_CORRECTION = 0.05;
const FONTSIZE = 15;
const LINE_SPACE_HEIGHT = 2;
const LINE_HEIGHT = 1;

interface IIoGraphDigitalProps {
  ioTrend: IIoTrend;
  lastTrendData: number | undefined;
  // raw period start, kept unadjusted as an anchor value for the ALL-period case,
  // where graphStart is intentionally left undefined (no boundary)
  selectedStart: number | undefined;
}

const mapStateToProps = (state: IState) => {
  const { selectedStart } = getGraphBoundaryDatesFromPeriod(
    state.gui.ioPeriod,
    state.site.tzOffset,
  );
  const props: IIoGraphDigitalProps = {
    ioTrend: state.trending.ioTrendFull,
    lastTrendData: state.trending.maxTrendTimestamp,
    selectedStart,
  };
  return { ...mapBaseStateToProps(state), ...props };
};

const messages = defineMessages({
  labelOn: {
    id: "On",
  },
  labelOff: {
    id: "Off",
  },
  labelMixed: {
    id: "Mixed",
  },
  timeOn: {
    id: "On Time",
  },
  timeOff: {
    id: "Off Time",
  },
  activities: {
    id: "Activities",
  },
});

interface IGraphLayout {
  fontSize: number;
  maxLineWidth: number;
  maxGraphWidth: number | undefined;
  maxY: number;
  padding: number;
}

interface ITimeStats {
  sumTimeOff: number;
  sumTimeOn: number;
  count: number;
}

// timeline of digital IOs: one on/off bar per IO, with switching statistics
class IoGraphDigitalComponent extends IoGraphBaseComponent<IIoGraphDigitalProps> {
  protected readonly ioType = "digital";

  private timeStats: ITimeStats[] = []; // for each selected IO

  protected preProcessData() {
    const startTime = this.props.graphStart!;
    const endTime = this.props.graphEnd!;
    const timestamps = Object.keys(this.props.ioTrend).map((item) => parseInt(item));

    this.timeStats = this.selectedIos.map(() => ({ sumTimeOn: 0, sumTimeOff: 0, count: 0 }));
    this.data = this.selectedIos.map((selectedIo, dataIndex) => {
      if (selectedIo.visible !== true) return [];

      const timeStat = this.timeStats[dataIndex];
      const sourceIndex = selectedIo.source === "input" ? 0 : 1;
      const isInverted =
        selectedIo.source === "output" &&
        (this.parameter[dataIndex] as IOutput | undefined)?.mode === OutputMode.OPEN;
      const rawValueAt = (timestamp: number) =>
        this.props.ioTrend[timestamp]?.[sourceIndex]?.[selectedIo.index] as number | undefined;
      const valueAt = (timestamp: number) => {
        const value = rawValueAt(timestamp);
        return isInverted && isSet(value) ? Number(!value) : value;
      };
      // neighbor state before/after the period
      const firstEntry = timestamps.find((ts) => ts < startTime && rawValueAt(ts) !== undefined);
      const lastEntry = timestamps.find((ts) => ts >= endTime && rawValueAt(ts) !== undefined);

      const values: number[][] = [];
      let lastState: number | undefined;
      let lastTimestamp: number | undefined;
      // accumulate on/off times up to the given timestamp
      const addTime = (timestamp: number) => {
        if (!isSet(lastTimestamp)) return;
        if (lastState === 1) timeStat.sumTimeOn += timestamp - lastTimestamp;
        else timeStat.sumTimeOff += timestamp - lastTimestamp;
      };

      timestamps
        .filter((ts) => ts >= startTime && ts < endTime && !isNaN(rawValueAt(ts)!))
        .forEach((ts, ind) => {
          const value = valueAt(ts)!;
          if (ind === 0 && isSet(firstEntry) && isSet(valueAt(firstEntry))) {
            // state before the period, so the line starts at the graph border
            values.push([firstEntry, valueAt(firstEntry)!]);
          }
          values.push([ts, value]);
          timeStat.count += 1;
          addTime(ts);
          lastTimestamp = ts;
          lastState = value;
        });

      if (values.length === 0 && startTime) {
        // no value within the period: show the first known state over the whole period
        const knownEntry = firstEntry ?? lastEntry;
        if (isSet(knownEntry)) {
          lastState = valueAt(knownEntry) ?? 0;
          lastTimestamp = startTime;
          values.push([startTime, lastState]);
        }
      }

      if (isSet(lastState) && isSet(lastTimestamp) && isSet(this.props.lastTrendData) && endTime) {
        // continue the last state up to the end of the chart (or last received IO data)
        const endDate = Math.max(Math.min(this.props.lastTrendData, endTime), lastTimestamp);
        values.push([endDate, lastState]);
        addTime(endDate);
      }
      return values;
    });
  }

  private prepareGraphLayout(): IGraphLayout {
    const maxGraphHeight = this.compRef.current?.clientHeight ?? 0;
    const maxGraphWidth = this.compRef.current?.clientWidth;
    const lineCnt = this.selectedIos.filter((curItem) => curItem.visible).length;
    const maxLineWidth = Math.min(maxGraphHeight / lineCnt / 3, MAX_LINE_WIDTH);
    return {
      fontSize: FONTSIZE - lineCnt * FONTSIZE_REDUCTION,
      maxLineWidth,
      maxGraphWidth,
      maxY: lineCnt * LINE_SPACE_HEIGHT + lineCnt * OFFSET_LINECOUNT_CORRECTION,
      padding: 1 + maxLineWidth / LINE_SPACE_HEIGHT - lineCnt * OFFSET_LINECOUNT_CORRECTION,
    };
  }

  // IO names on the left and switching statistics on the right of each line
  private createGraphLabels(graphLayout: IGraphLayout) {
    const labels: AnnotationOptions[] = [];
    const font = {
      family: "Arial",
      size: graphLayout.fontSize,
      style: "normal" as const,
      lineHeight: 1,
      weight: null,
    };
    let lineIndex = 0;
    this.parameter.forEach((aParameter, dataIndex) => {
      const selectedIo = this.selectedIos[dataIndex];
      if (selectedIo?.visible !== true) return;

      const timeStat = this.timeStats[dataIndex];
      const width = graphLayout.maxGraphWidth;
      const contentText =
        width && width < 320 ? ""
        : width && width < 480 ?
          `${formatTimestamp(timeStat.sumTimeOn)}, ${formatTimestamp(timeStat.sumTimeOff)}, ${timeStat.count}`
        : `${this.context.formatMessage(messages.timeOn)}: ${formatTimestamp(timeStat.sumTimeOn)}, ` +
          `${this.context.formatMessage(messages.timeOff)}: ${formatTimestamp(timeStat.sumTimeOff)}, ` +
          `${this.context.formatMessage(messages.activities)}: ${timeStat.count}`;

      labels.push({
        type: "label",
        xValue: this.startTime ? this.startTime * 1000 : this.props.selectedStart,
        yValue: lineIndex * LINE_SPACE_HEIGHT + 1,
        padding: graphLayout.padding,
        color: getColor(selectedIo.index),
        position: { x: "start", y: "end" },
        backgroundColor: cssContent.DROPDOWN_TRANSPARENT,
        content: aParameter?.name ?? "----",
        font,
      });
      labels.push({
        type: "label",
        xValue: this.endTime ? this.endTime * 1000 : this.props.maxX,
        yValue: lineIndex * LINE_SPACE_HEIGHT + LINE_HEIGHT,
        padding: graphLayout.padding,
        color: getColor(selectedIo.index),
        position: { x: "end", y: "end" },
        backgroundColor: cssContent.DROPDOWN_TRANSPARENT,
        content: contentText,
        font,
      });
      lineIndex = lineIndex + 1;
    });
    return labels;
  }

  protected createGraphOptions() {
    const graphLayout = this.prepareGraphLayout();
    const options: ChartOptions<IoChartType> = {
      // line widths and labels depend on the graph size
      onResize: (chart) => this.resizeGraph(chart),
      scales: {
        x: this.createTimeScale(),
        y: {
          type: "linear",
          max: graphLayout.maxY,
          min: 0,
          beginAtZero: true,
          display: false,
        },
      },
      animation: {
        duration: 0,
      },
      maintainAspectRatio: false, // needed to fit size in status screen
      elements: {
        point: {
          radius: 0,
        },
      },
      plugins: {
        legend: {
          display: false, // IO names are shown as labels
        },
        annotation: {
          annotations: this.createGraphLabels(graphLayout),
        },
        tooltip: {
          enabled: true,
          mode: "point",
          position: "nearest", // show tooltip on average value, not at mouse cursor
          intersect: false, // no need to exactly match the point to show the tooltip
          itemSort: (a, b) => b.datasetIndex - a.datasetIndex,
          callbacks: {
            title: (tooltipItems) => this.createTooltipTitle(tooltipItems),
            label: (tooltipItem) => {
              // do we face a background fill data set?
              if (
                tooltipItem.dataset.label === undefined ||
                this.selectedIos[tooltipItem.datasetIndex]?.visible !== true
              ) {
                return undefined as unknown as string; // we need to disable the wrong type check here
              }
              const value = this.data[tooltipItem.datasetIndex][tooltipItem.dataIndex]?.[1];
              const label =
                value === 0 ? messages.labelOff
                : value === 1 ? messages.labelOn
                : messages.labelMixed;
              return ` ${tooltipItem.dataset.label}: ${this.context.formatMessage(label)}`;
            },
          },
        },
        zoom: this.createZoomOptions(),
        datalabels: {
          display: false,
        },
      },
    };
    return options;
  }

  protected createGraphData() {
    const graphLayout = this.prepareGraphLayout();
    const data: ChartData<IoChartType, ScatterDataPoint[], unknown> = {
      datasets: [],
    };
    let lineIndex = 0;
    // hidden IOs get an empty data set, so data set indices match the selected IOs (tooltip)
    this.data.forEach((dataset, anIndex) => {
      const selectedIo = this.selectedIos[anIndex];
      const isVisible = selectedIo?.visible === true;

      // each visible IO gets its own line, the segment color shows the state
      const lineY = LINE_SPACE_HEIGHT * lineIndex + LINE_HEIGHT;
      data.datasets.push({
        label: this.parameter[anIndex]?.name,
        hidden: !isVisible,
        data: dataset.map((curItem) => ({
          x: curItem[0] * 1000, // timestamp
          y: curItem[1] ? lineY + 0.0000000001 : lineY,
        })),
        type: "line",
        stepped: true,
        fill: false,
        segment: {
          // on: graph color, off: overshoot color
          borderColor: (ctx) => {
            const value0 = dataset[ctx.p0DataIndex]?.[1];
            const value1 = dataset[ctx.p1DataIndex]?.[1];
            if (value1 < value0) {
              return cssContent.GRAPH_COLOR;
            } else if (value1 === value0) {
              return value1 === 1 ? cssContent.GRAPH_COLOR : cssContent.GRAPH_COLOR_OVERSHOOT;
            }
            return cssContent.GRAPH_COLOR_OVERSHOOT;
          },
        },
        borderWidth: graphLayout.maxLineWidth,
        borderColor: cssContent.DROPDOWN_TRANSPARENT,
        backgroundColor: getColor(selectedIo?.index ?? 0),
      });
      if (isVisible) lineIndex = lineIndex + 1;
    });
    return data;
  }

  private resizeGraph(chart: ChartJS) {
    chart.data = this.createGraphData();
    chart.options = this.createGraphOptions() as ChartOptions;
    chart.update();
  }
}

const IoGraphDigital = connect(mapStateToProps, mapDispatchToProps)(IoGraphDigitalComponent);

export default IoGraphDigital;
